import {
  Badge,
  Table,
  EmptyState,
  ErrorBanner,
  Button,
} from "frontend-kit/components/index.js";
import { onChainCategoryLabel, onChainCategoryStyle } from "./category-colors.js";
import { getChainApp, getBlockHeight, getWallet } from "./chain-app.js";
import { luxToDusk, isContractOwner } from "./chain-config.js";
import { duskAmountNode } from "./dusk-amount.js";
import {
  getMarket,
  listMarketTrades,
  listMarketHistory,
  loadBackendConfig,
  setMarketHidden,
} from "./gateway-api.js";
import { loadTradingKey } from "./trading-key-store.js";
import { hexToBytes } from "./trader-crypto.js";
import { renderTradeTicket } from "./trade-ticket.js";
import { renderResolutionPanel } from "./resolution-panel.js";
import { pollCached } from "./data-cache.js";
import { RULES_COURT_LINES } from "./privacy-copy.js";
import { renderTxCell } from "./tx-cell.js";
import { icon } from "./icons.js";
import {
  STATUS_ORDER,
  STATUS_LABELS,
  PHASE_DESCRIPTIONS,
  effectiveStatus,
  effectiveResolution,
  statusBadgeVariant,
  resolutionSummary,
  outcomeLabel,
  truncHex,
  windowBlocksRemaining,
} from "./market-status.js";

function priceYesToPercent(price1e18) {
  if (price1e18 == null) return null;
  const n = Number(price1e18) / 1e18;
  if (Number.isNaN(n)) return null;
  return Math.round(n * 1000) / 10;
}

/** FPMM spot: P(yes) = no / (yes + no). */
function priceYesFromReserves(info) {
  if (!info) return null;
  const yes = Number(info.yes_reserve);
  const no = Number(info.no_reserve);
  const sum = yes + no;
  if (!sum || Number.isNaN(sum)) return null;
  return Math.round((no / sum) * 1000) / 10;
}

function trunc(s, n = 10) {
  if (!s) return "-";
  return s.length <= n + 1 ? s : `${s.slice(0, n)}…`;
}

function statCell(label, value) {
  const wrap = document.createElement("div");
  wrap.className = "stat-cell";
  const l = document.createElement("div");
  l.className = "stat-label";
  l.textContent = label;
  const v = document.createElement("div");
  v.className = "stat-value";
  if (typeof value === "string") {
    v.textContent = value;
  } else {
    v.append(value);
  }
  wrap.append(l, v);
  return wrap;
}

function splitStat(label, total, yesValue, noValue) {
  const stack = document.createElement("div");
  stack.className = "stat-value-stack";
  const totalEl = document.createElement("div");
  totalEl.className = "stat-total";
  totalEl.append(total);
  const split = document.createElement("div");
  split.className = "stat-split";
  const yes = document.createElement("span");
  yes.className = "stat-split-yes";
  yes.append("YES ", yesValue);
  const sep = document.createElement("span");
  sep.className = "stat-split-sep";
  sep.textContent = "·";
  const no = document.createElement("span");
  no.className = "stat-split-no";
  no.append("NO ", noValue);
  split.append(yes, sep, no);
  stack.append(totalEl, split);
  return statCell(label, stack);
}

function outcomeBreakdown(trades) {
  let yesCount = 0;
  let noCount = 0;
  let yesVol = 0n;
  let noVol = 0n;
  for (const t of trades) {
    const amt = BigInt(t.amount ?? 0);
    if (t.outcome === 0) {
      yesCount += 1;
      yesVol += amt;
    } else if (t.outcome === 1) {
      noCount += 1;
      noVol += amt;
    }
  }
  return { yesCount, noCount, yesVol, noVol, total: yesCount + noCount };
}

function renderCategoryPill(marketCategory) {
  const label = onChainCategoryLabel(marketCategory);
  if (!label) return null;
  const cat = onChainCategoryStyle(marketCategory);
  const pill = document.createElement("span");
  pill.className = "dt-pill wen-cat-pill";
  pill.style.background = cat.bg;
  pill.style.color = cat.fg;
  pill.textContent = label;
  return pill;
}

/**
 * @param {number} statusIdx index into STATUS_ORDER of the market's actual
 *   current phase
 * @param {number} selectedIdx index of the phase whose detail is shown
 *   below - usually the same as statusIdx, but the viewer can click back
 *   to an earlier phase (e.g. `under_review` on a since-finalized market)
 * @param {(i: number) => void} onSelect
 */
function renderLifecycleBar(statusIdx, selectedIdx, onSelect) {
  const bar = document.createElement("div");
  bar.className = "lifecycle-bar";
  bar.setAttribute("role", "list");
  STATUS_ORDER.forEach((key, i) => {
    const step = document.createElement("button");
    step.type = "button";
    step.className = "lifecycle-step";
    step.setAttribute("role", "listitem");
    if (i < statusIdx) step.classList.add("is-done");
    else if (i === statusIdx) {
      step.classList.add("is-active");
      step.setAttribute("aria-current", "step");
    } else {
      step.classList.add("is-pending");
    }
    if (i === selectedIdx) step.classList.add("is-selected");
    step.title = `View ${STATUS_LABELS[key] ?? key} details`;
    step.addEventListener("click", () => onSelect(i));
    const name = document.createElement("span");
    name.className = "lifecycle-step-label";
    name.textContent = STATUS_LABELS[key] ?? key;
    step.append(name);
    bar.append(step);
  });
  return bar;
}

/** Renders one `resolutionSummary()` row's tagged value (see market-status.js). */
function renderPhaseSummaryValue(value) {
  switch (value.kind) {
    case "outcome":
      return outcomeLabel(value.outcome);
    case "dusk":
      return duskAmountNode(value.lux);
    case "hex":
      return truncHex(value.hex);
    default:
      return value.text ?? "-";
  }
}

/**
 * Detail panel for whichever phase is selected in the lifecycle bar.
 * Read-only status/info - the actionable buttons (assert/dispute/
 * challenge/resolve) stay in `resolution-panel.js`, not duplicated here.
 *
 * @param {number} selectedIdx
 * @param {number} statusIdx market's actual current phase index
 * @param {object|null} resolutionView same shape resolution-panel.js reads
 * @param {{ closeBlock?: number|null, blockHeight?: number|null }} opts
 */
function renderPhaseDetailPanel(selectedIdx, statusIdx, resolutionView, { closeBlock, blockHeight }) {
  const key = STATUS_ORDER[selectedIdx];
  const panel = document.createElement("div");
  panel.className = "info-card phase-detail-panel";

  const head = document.createElement("div");
  head.className = "info-card-head";
  const title = document.createElement("strong");
  title.textContent = STATUS_LABELS[key] ?? key;
  head.append(title);
  if (selectedIdx === statusIdx) {
    head.append(Badge({ variant: statusBadgeVariant(key), label: "Current phase" }));
  } else if (selectedIdx < statusIdx) {
    head.append(Badge({ variant: "neutral", label: "Past phase" }));
  } else {
    head.append(Badge({ variant: "neutral", label: "Not reached yet" }));
  }
  panel.append(head);

  const desc = document.createElement("p");
  desc.className = "hint phase-detail-desc";
  desc.textContent = PHASE_DESCRIPTIONS[key] ?? "";
  panel.append(desc);

  if (key === "open" && closeBlock != null) {
    const p = document.createElement("p");
    p.className = "hint";
    if (blockHeight != null) {
      const remaining = Number(closeBlock) - Number(blockHeight);
      p.textContent =
        remaining > 0
          ? `Closes at block ${closeBlock} (${remaining.toLocaleString()} blocks from now).`
          : `Close block ${closeBlock} has passed - this market has moved on to Pending NO.`;
    } else {
      p.textContent = `Closes at block ${closeBlock}.`;
    }
    panel.append(p);
  }

  // Live countdown for challenge / review windows when this is the current phase.
  if (selectedIdx === statusIdx) {
    const win = windowBlocksRemaining(resolutionView, key, blockHeight);
    if (win) {
      const p = document.createElement("p");
      p.className = "hint phase-window-countdown";
      const label = win.kind === "review" ? "Review window" : "Challenge window";
      if (win.remaining > 0) {
        p.textContent = `${label}: ${win.remaining.toLocaleString()} blocks left (ends ~block ${win.endBlock}).`;
      } else {
        p.textContent = `${label} ended - finalize is eligible if still pending.`;
      }
      panel.append(p);
    }
  }

  // Resolution fields (window/bond/dispute/winner) reflect the market's
  // *current* on-chain resolution state regardless of which phase is
  // selected - the contract doesn't keep separate historical snapshots
  // per phase, but fields like the dispute record and winning outcome
  // persist through to `finalized`, so clicking back to e.g.
  // `under_review` on a finalized market still shows what happened there.
  // For future ("Not reached yet") phases, skip live resolution fields so
  // they don't read as already happened.
  if (selectedIdx <= statusIdx) {
    const grid = document.createElement("div");
    grid.className = "resolution-grid";
    for (const row of resolutionSummary(resolutionView)) {
      grid.append(statCell(row.label, renderPhaseSummaryValue(row.value)));
    }
    if (grid.childNodes.length) panel.append(grid);
  }

  return panel;
}

/** Always-visible rules: criteria + court path (above the fold). */
function renderRulesCard(metadata) {
  const card = document.createElement("div");
  card.className = "info-card rules-card";

  const head = document.createElement("div");
  head.className = "info-card-head";
  const title = document.createElement("strong");
  title.textContent = "Rules";
  head.append(title);
  card.append(head);

  const criteria = metadata?.resolution_criteria?.trim();
  const criteriaEl = document.createElement("p");
  criteriaEl.className = "rules-criteria";
  criteriaEl.textContent = criteria || "No criteria provided.";
  card.append(criteriaEl);

  const list = document.createElement("ul");
  list.className = "rules-court-list";
  for (const line of RULES_COURT_LINES) {
    const li = document.createElement("li");
    li.textContent = line;
    list.append(li);
  }
  card.append(list);
  return card;
}

/** Info panel body (About / description) for chip expander. */
function renderInfoPanelBody(metadata) {
  const description = metadata?.description?.trim() || "";
  const criteria = metadata?.resolution_criteria?.trim() || "";
  const card = document.createElement("div");
  card.className = "info-card info-expand-card is-collapsed";
  if (!description && !criteria) {
    const empty = document.createElement("p");
    empty.className = "hint";
    empty.textContent = "No description provided.";
    card.append(empty);
    return card;
  }
  const sameText =
    description && criteria && description.toLowerCase() === criteria.toLowerCase();

  const preview = document.createElement("div");
  preview.className = "info-preview";

  if (description) {
    const dLabel = document.createElement("div");
    dLabel.className = "stat-label";
    dLabel.textContent = "Description";
    const dBody = document.createElement("p");
    dBody.className = "desc-card-body";
    dBody.textContent = description;
    preview.append(dLabel, dBody);
  }

  const extra = document.createElement("div");
  extra.className = "info-preview-extra";
  if (criteria && !sameText) {
    const cLabel = document.createElement("div");
    cLabel.className = "stat-label";
    cLabel.textContent = "Resolution";
    const cBody = document.createElement("p");
    cBody.className = "desc-card-body";
    cBody.textContent = criteria;
    extra.append(cLabel, cBody);
  } else if (criteria && !description) {
    const cLabel = document.createElement("div");
    cLabel.className = "stat-label";
    cLabel.textContent = "Resolution";
    const cBody = document.createElement("p");
    cBody.className = "desc-card-body";
    cBody.textContent = criteria;
    extra.append(cLabel, cBody);
  }

  card.append(preview);
  if (extra.childNodes.length) card.append(extra);

  const more = document.createElement("button");
  more.type = "button";
  more.className = "info-preview-more";
  more.textContent = "Show more";
  more.setAttribute("aria-expanded", "false");
  more.addEventListener("click", (e) => {
    e.stopPropagation();
    const open = card.classList.toggle("is-expanded");
    card.classList.toggle("is-collapsed", !open);
    more.textContent = open ? "Show less" : "Show more";
    more.setAttribute("aria-expanded", open ? "true" : "false");
  });
  card.append(more);
  return card;
}

/**
 * Info | Rules | Phase tabs. Info is the default open panel.
 * Phase holds the current/selected lifecycle detail (Open, windows, …).
 * @returns {{ root: HTMLElement, showPhase: () => void, refresh: () => void }}
 */
function renderInfoRulesChips({ metadata, getPhasePanel } = {}) {
  const root = document.createElement("div");
  root.className = "detail-meta-chips";

  const row = document.createElement("div");
  row.className = "meta-chip-row";
  row.setAttribute("role", "tablist");
  row.setAttribute("aria-label", "Event details");

  const panels = document.createElement("div");
  panels.className = "meta-chip-panels";

  let active = "info";
  const chips = {};

  function paintPanels() {
    panels.replaceChildren();
    if (active === "info") panels.append(renderInfoPanelBody(metadata));
    else if (active === "rules") panels.append(renderRulesCard(metadata));
    else if (active === "phase" && getPhasePanel) {
      panels.append(getPhasePanel());
    }
    for (const [key, btn] of Object.entries(chips)) {
      const on = key === active;
      btn.classList.toggle("is-active", on);
      btn.setAttribute("aria-selected", on ? "true" : "false");
    }
  }

  function makeChip(key, label, iconName, toneClass) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `meta-chip ${toneClass}`;
    btn.setAttribute("role", "tab");
    btn.setAttribute("aria-selected", "false");
    btn.append(icon(iconName, { size: 14 }), document.createTextNode(label));
    btn.addEventListener("click", () => {
      active = key;
      paintPanels();
    });
    chips[key] = btn;
    return btn;
  }

  row.append(
    makeChip("info", "Info", "feedback", "meta-chip--info"),
    makeChip("rules", "Rules", "alert", "meta-chip--rules"),
    makeChip("phase", "Phase", "block", "meta-chip--phase")
  );
  root.append(row, panels);
  paintPanels();
  return {
    root,
    showPhase() {
      active = "phase";
      paintPanels();
    },
    refresh() {
      if (active !== "phase") return;
      paintPanels();
    },
  };
}

/**
 * Plain-language FPMM explainer - what the reserves/price/slippage stats
 * actually mean. Native <details> - no JS state needed for a static
 * explainer. Confirmed against the contract before writing this: 0.3.0
 * has no add/remove-liquidity or LP-token mechanics (grepped
 * prediction-market/src for lp_token/liquidity_provider/add_liquidity -
 * none) - pool is seeded once at create_market and only moves via trades.
 */
function renderLiquidityExplainer() {
  const details = document.createElement("details");
  details.className = "info-card liquidity-explainer";
  const summary = document.createElement("summary");
  summary.textContent = "How odds & pool liquidity work";
  details.append(summary);

  const body = document.createElement("div");
  body.className = "desc-card-body";
  const items = [
    [
      "What the reserves are",
      "yes_reserve/no_reserve are share counts sitting in the pool, seeded once when the event is created (initial_liquidity, split evenly). There's no separate liquidity-provider/LP-token system in this version - the pool only moves because people trade, not because anyone adds or withdraws liquidity.",
    ],
    [
      "Why the price is no/(yes+no)",
      "YES price = no_reserve / (yes_reserve + no_reserve) - the more a side's reserve has been bought down relative to the other, the higher that side's price. Both prices always sum to 1 (100%).",
    ],
    [
      "Why a bigger trade moves the price more",
      "Buying YES removes YES shares from the pool along a fixed curve (x·y = k), so the price you actually pay is the average over that move, not the price you saw before you traded - a small trade barely moves it, a trade that's a large fraction of the pool moves it a lot. Portfolio's \"Sell now\" figure already accounts for this; \"Mark\" is the naive spot-price estimate for comparison.",
    ],
  ];
  for (const [label, text] of items) {
    const h = document.createElement("div");
    h.className = "stat-label";
    h.textContent = label;
    const p = document.createElement("p");
    p.textContent = text;
    body.append(h, p);
  }
  details.append(body);
  return details;
}

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Each trade's `implied_price` is the price of whichever outcome it traded
 * (YES or NO), not always P(YES) - a YES-side trade's implied_price *is*
 * P(YES); a NO-side trade's implied_price is P(NO), so P(YES) there is
 * `1 - implied_price`. Build one P(YES) series from the mixed trade tape.
 */
function priceHistorySeries(points) {
  const out = [];
  for (const p of points ?? []) {
    const price = Number(p.implied_price);
    if (p.implied_price == null || Number.isNaN(price)) continue;
    const pYes = p.outcome === 1 ? 1 - price : price;
    if (pYes < 0 || pYes > 1) continue;
    out.push({ x: Number(p.block_number) || 0, yes: pYes });
  }
  out.sort((a, b) => a.x - b.x);
  return out;
}

function svgEl(tag, attrs = {}) {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

/** Two-line YES/NO price-history chart: axes, gridlines, legend, direct end
 * labels, and a hover crosshair+tooltip. YES/NO are color-coded (mint/rose)
 * *and* line-style-coded (solid/dashed) since that hue pair alone doesn't
 * clear a colorblind-safe separation threshold - see polish plan Track 4. */
function renderPriceChart(history) {
  const series = priceHistorySeries(history);
  if (series.length < 2) return null;

  const root = document.createElement("div");
  root.className = "price-chart";

  const legend = document.createElement("div");
  legend.className = "price-chart-legend";
  for (const side of [
    { key: "yes", label: "YES", cls: "is-yes" },
    { key: "no", label: "NO", cls: "is-no" },
  ]) {
    const item = document.createElement("span");
    item.className = `price-chart-legend-item ${side.cls}`;
    const swatch = document.createElement("span");
    swatch.className = "price-chart-legend-swatch";
    const text = document.createElement("span");
    text.textContent = side.label;
    item.append(swatch, text);
    legend.append(item);
  }
  root.append(legend);

  const w = 640;
  const h = 220;
  const padL = 34;
  const padR = 46;
  const padT = 10;
  const padB = 22;
  const plotW = w - padL - padR;
  const plotH = h - padT - padB;

  const xs = series.map((s) => s.x);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const spanX = maxX - minX || 1;

  function xAt(x) {
    return padL + ((x - minX) / spanX) * plotW;
  }
  function yAt(p) {
    return padT + (1 - p) * plotH;
  }

  const svg = svgEl("svg", { viewBox: `0 0 ${w} ${h}`, class: "price-chart-svg" });

  // Recessive gridlines + y-axis labels at 0/50/100%.
  for (const pct of [0, 0.5, 1]) {
    const y = yAt(pct);
    svg.append(
      svgEl("line", {
        x1: padL,
        x2: w - padR,
        y1: y,
        y2: y,
        class: "price-chart-grid",
      })
    );
    const label = svgEl("text", {
      x: padL - 8,
      y: y + 3,
      class: "price-chart-axis-label",
      "text-anchor": "end",
    });
    label.textContent = `${Math.round(pct * 100)}¢`;
    svg.append(label);
  }

  // x-axis: first/last block number.
  for (const [x, anchor, blockX] of [
    [padL, "start", minX],
    [w - padR, "end", maxX],
  ]) {
    const label = svgEl("text", {
      x,
      y: h - 4,
      class: "price-chart-axis-label",
      "text-anchor": anchor,
    });
    label.textContent = `#${blockX.toLocaleString()}`;
    svg.append(label);
  }

  function linePath(getY) {
    return series.map((s, i) => `${i === 0 ? "M" : "L"}${xAt(s.x)},${getY(s)}`).join(" ");
  }

  const yesPath = svgEl("path", {
    d: linePath((s) => yAt(s.yes)),
    class: "price-chart-line is-yes",
  });
  const noPath = svgEl("path", {
    d: linePath((s) => yAt(1 - s.yes)),
    class: "price-chart-line is-no",
  });
  svg.append(yesPath, noPath);

  // Direct end-labels (selective - only the last point, not every point).
  const last = series[series.length - 1];
  const yesEndY = yAt(last.yes);
  const noEndY = yAt(1 - last.yes);
  const endYes = svgEl("text", {
    x: w - padR + 6,
    y: yesEndY + 3,
    class: "price-chart-end-label is-yes",
  });
  endYes.textContent = `YES ${Math.round(last.yes * 100)}¢`;
  const endNo = svgEl("text", {
    x: w - padR + 6,
    y: noEndY + 3,
    class: "price-chart-end-label is-no",
  });
  endNo.textContent = `NO ${Math.round((1 - last.yes) * 100)}¢`;
  svg.append(endYes, endNo);

  // Hover layer: crosshair + two markers, driven by pointer position.
  const crosshair = svgEl("line", {
    y1: padT,
    y2: h - padB,
    class: "price-chart-crosshair",
  });
  const yesDot = svgEl("circle", { r: 3.5, class: "price-chart-dot is-yes" });
  const noDot = svgEl("circle", { r: 3.5, class: "price-chart-dot is-no" });
  crosshair.style.display = "none";
  yesDot.style.display = "none";
  noDot.style.display = "none";
  svg.append(crosshair, yesDot, noDot);

  const hitArea = svgEl("rect", {
    x: padL,
    y: padT,
    width: plotW,
    height: plotH,
    fill: "transparent",
    class: "price-chart-hit",
  });
  svg.append(hitArea);

  const tooltip = document.createElement("div");
  tooltip.className = "price-chart-tooltip";
  tooltip.hidden = true;

  function nearestIndex(clientX) {
    const rect = svg.getBoundingClientRect();
    const localX = ((clientX - rect.left) / rect.width) * w;
    let best = 0;
    let bestDist = Infinity;
    series.forEach((s, i) => {
      const d = Math.abs(xAt(s.x) - localX);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    return best;
  }

  function showAt(i, clientX, clientY) {
    const s = series[i];
    const x = xAt(s.x);
    crosshair.setAttribute("x1", x);
    crosshair.setAttribute("x2", x);
    yesDot.setAttribute("cx", x);
    yesDot.setAttribute("cy", yAt(s.yes));
    noDot.setAttribute("cx", x);
    noDot.setAttribute("cy", yAt(1 - s.yes));
    crosshair.style.display = "";
    yesDot.style.display = "";
    noDot.style.display = "";

    tooltip.replaceChildren();
    const head = document.createElement("div");
    head.className = "price-chart-tooltip-head";
    head.textContent = `Block #${s.x.toLocaleString()}`;
    const yesRow = document.createElement("div");
    yesRow.className = "price-chart-tooltip-row is-yes";
    yesRow.textContent = `YES ${Math.round(s.yes * 100)}¢`;
    const noRow = document.createElement("div");
    noRow.className = "price-chart-tooltip-row is-no";
    noRow.textContent = `NO ${Math.round((1 - s.yes) * 100)}¢`;
    tooltip.append(head, yesRow, noRow);
    tooltip.hidden = false;

    const rootRect = root.getBoundingClientRect();
    tooltip.style.left = `${clientX - rootRect.left + 12}px`;
    tooltip.style.top = `${clientY - rootRect.top - 12}px`;
  }

  function hideHover() {
    crosshair.style.display = "none";
    yesDot.style.display = "none";
    noDot.style.display = "none";
    tooltip.hidden = true;
  }

  hitArea.addEventListener("pointermove", (e) => {
    showAt(nearestIndex(e.clientX), e.clientX, e.clientY);
  });
  hitArea.addEventListener("pointerleave", hideHover);

  const frame = document.createElement("div");
  frame.className = "price-chart-frame";
  frame.append(svg, tooltip);
  root.append(frame);
  return root;
}

export async function renderMarketDetail({
  marketId,
  log,
  onBack,
  onRefresh,
  onCollateralChange,
  registerCleanup,
}) {
  const root = document.createElement("div");
  root.className = "detail-root";
  const cfg = loadBackendConfig();

  let market;
  let info = null;
  let resolution = null;
  let priceYes = null;
  let trades = [];
  let history = [];
  let position = [0, 0];
  let blockHeight = null;
  let chainErr = null;

  try {
    market = await getMarket(cfg, marketId);
  } catch (e) {
    root.append(ErrorBanner({ message: e.message || String(e) }));
    root.append(
      Button({
        label: "← Events",
        onClick: () => onBack?.(),
      })
    );
    return root;
  }

  // First paint uses gateway market only - chain reads + tape load in the
  // background so the page isn't stuck on "Loading…" while the node answers.
  let enrichDone = false;
  let cancelled = false;

  // Mutable so refreshLiveData() (live-poll wiring, further down) can
  // recompute these in place after a fresh read instead of the page only
  // ever reflecting whatever was true at initial load.
  let pct, closeBlock, status, statusIdx, resolutionView;
  function computeDerived() {
    pct = priceYesToPercent(priceYes) ?? priceYesFromReserves(info);
    closeBlock = info?.close_block ?? null;
    status = effectiveStatus(resolution?.status ?? market.status ?? "open", {
      closeBlock,
      blockHeight,
    });
    statusIdx = STATUS_ORDER.indexOf(status);
    if (statusIdx < 0) statusIdx = 0;
    resolutionView = effectiveResolution(resolution, status, { closeBlock });
  }
  computeDerived();
  let breakdown = outcomeBreakdown(trades);

  const layout = document.createElement("div");
  layout.className = "detail-layout";

  const primary = document.createElement("div");
  primary.className = "detail-primary";

  const back = Button({
    label: "← Events",
    onClick: () => onBack?.(),
  });
  back.classList.add("detail-back");
  primary.append(back);

  const headerBand = document.createElement("div");
  headerBand.className = "detail-header-band";
  const catPill = renderCategoryPill(market.market_category);
  if (catPill) headerBand.append(catPill);

  const title = document.createElement("h1");
  title.className = "detail-title";
  title.textContent = market.metadata?.title ?? `Event #${marketId}`;
  headerBand.append(title);

  const metaRow = document.createElement("div");
  metaRow.className = "detail-meta-row";
  if (market.metadata?.verified === false) {
    metaRow.append(Badge({ variant: "danger", label: "Unverified" }));
  }
  if (market.hidden) {
    metaRow.append(Badge({ variant: "neutral", label: "Hidden from Browse" }));
  }
  if (metaRow.childNodes.length) headerBand.append(metaRow);
  if (isContractOwner(getWallet())) {
    const hideBtn = Button({
      label: market.hidden ? "Unhide from Browse" : "Hide from Browse",
      variant: "secondary",
      onClick: async () => {
        hideBtn.disabled = true;
        try {
          await setMarketHidden(cfg, marketId, !market.hidden);
          log?.(`event #${marketId} ${market.hidden ? "unhidden" : "hidden"}`);
          onRefresh?.();
        } catch (e) {
          log?.(`hide toggle failed: ${e.message || e}`);
          hideBtn.disabled = false;
        }
      },
    });
    hideBtn.classList.add("detail-hide-btn");
    headerBand.append(hideBtn);
  }
  primary.append(headerBand);

  const lifecycleWrap = document.createElement("div");
  lifecycleWrap.className = "lifecycle-bar-wrap";
  let selectedPhaseIdx = statusIdx;
  const infoRules = renderInfoRulesChips({
    metadata: market.metadata,
    getPhasePanel: () =>
      renderPhaseDetailPanel(selectedPhaseIdx, statusIdx, resolutionView, {
        closeBlock,
        blockHeight,
      }),
  });
  function paintLifecycle() {
    lifecycleWrap.replaceChildren(
      renderLifecycleBar(statusIdx, selectedPhaseIdx, (i) => {
        selectedPhaseIdx = i;
        paintLifecycle();
        infoRules.showPhase();
      })
    );
    infoRules.refresh();
  }
  paintLifecycle();
  primary.append(lifecycleWrap, infoRules.root);

  const chainErrHost = document.createElement("div");
  primary.append(chainErrHost);
  function paintChainErr() {
    chainErrHost.replaceChildren();
    if (chainErr) {
      chainErrHost.append(
        ErrorBanner({
          message: `On-chain reads failed (${chainErr}). Showing gateway data only.`,
        })
      );
    }
  }
  paintChainErr();

  // Rebuildable (not just built-once) so refreshLiveData() can reflect a
  // price/status change without remounting anything a user might be
  // mid-interaction with - this block is pure display, no inputs, so a
  // full rebuild on refresh is safe (unlike the trade ticket, which gets
  // targeted updates instead - see updateMarketData below).
  const oddsHost = document.createElement("div");
  primary.append(oddsHost);
  function paintOdds() {
    const winning =
      info?.winning_outcome ??
      market.winning_outcome ??
      resolutionView?.winning_outcome ??
      null;
    const yesPct = pct;
    const noPct = pct == null ? null : Math.round((100 - pct) * 10) / 10;

    const odds = document.createElement("div");
    odds.className = "odds-block";
    if (status === "finalized" && winning != null) {
      odds.classList.add("odds-block--resolved");
      odds.dataset.winner = winning === 0 ? "yes" : "no";
    }

    if (status === "finalized" && winning != null) {
      const resultHead = document.createElement("div");
      resultHead.className = "odds-result-head";
      resultHead.append(
        Badge({
          variant: winning === 0 ? "success" : "neutral",
          label: winning === 0 ? "Resolved · YES" : "Resolved · NO",
        })
      );
      const hist = document.createElement("span");
      hist.className = "odds-historical-label";
      hist.textContent =
        yesPct == null ? "Final odds unavailable" : `Last traded odds · historical`;
      resultHead.append(hist);
      odds.append(resultHead);
    }

    const oddsRow = document.createElement("div");
    oddsRow.className = "odds-row detail-yesno-hero";

    function sideBlock(side, label, percent, sideStats) {
      const el = document.createElement("div");
      el.className = `odds-side odds-${side}`;
      const isWinner =
        status === "finalized" &&
        winning != null &&
        ((side === "yes" && winning === 0) || (side === "no" && winning === 1));
      const isLoser = status === "finalized" && winning != null && !isWinner;
      if (isWinner) el.classList.add("is-winner");
      if (isLoser) el.classList.add("is-loser");

      const top = document.createElement("div");
      top.className = "odds-side-top";
      const name = document.createElement("span");
      name.className = "odds-side-name";
      name.textContent = label;
      const pctEl = document.createElement("span");
      pctEl.className = "odds-side-pct dt-val";
      pctEl.textContent = percent == null ? "-" : `${percent}¢`;
      top.append(name, pctEl);
      if (isWinner) {
        top.append(Badge({ variant: "success", label: "Won" }));
      }
      el.append(top);

      if (status === "finalized" && winning != null && sideStats) {
        const meta = document.createElement("div");
        meta.className = "odds-side-stats";
        meta.append(duskAmountNode(sideStats.vol, { unit: "dusk" }), ` · ${sideStats.trades} trades`);
        el.append(meta);
      }
      return el;
    }

    oddsRow.append(
      sideBlock("yes", "YES", yesPct, {
        vol: luxToDusk(breakdown.yesVol),
        trades: breakdown.yesCount,
      }),
      sideBlock("no", "NO", noPct, {
        vol: luxToDusk(breakdown.noVol),
        trades: breakdown.noCount,
      })
    );
    odds.append(oddsRow);

    if (yesPct != null) {
      const meter = document.createElement("div");
      meter.className = "odds-meter";
      if (status === "finalized" && winning != null) {
        meter.classList.add("odds-meter--historical");
        meter.dataset.winner = winning === 0 ? "yes" : "no";
      }
      meter.setAttribute("role", "img");
      meter.setAttribute(
        "aria-label",
        status === "finalized" && winning != null
          ? `Resolved ${winning === 0 ? "YES" : "NO"}. Last price ${yesPct}¢ YES, ${noPct}¢ NO`
          : `${yesPct}¢ YES, ${noPct}¢ NO`
      );
      const yesFill = document.createElement("div");
      yesFill.className = "odds-meter-yes";
      yesFill.style.width = `${yesPct}%`;
      const noFill = document.createElement("div");
      noFill.className = "odds-meter-no";
      noFill.style.width = `${100 - yesPct}%`;
      meter.append(yesFill, noFill);
      odds.append(meter);
    }
    oddsHost.replaceChildren(odds);
  }
  paintOdds();

  const posCard = document.createElement("div");
  posCard.className = "position-card";
  const posLabel = document.createElement("div");
  posLabel.className = "stat-label";
  posLabel.textContent = "Your position";
  const posVals = document.createElement("div");
  posVals.className = "position-card-vals";
  const yesPos = document.createElement("div");
  yesPos.className = "position-card-yes";
  const yesLabel = document.createElement("span");
  yesLabel.textContent = "YES";
  const yesStrong = document.createElement("strong");
  yesStrong.append(duskAmountNode(position?.[0] ?? 0, { decimals: 4 }));
  yesPos.append(yesLabel, yesStrong);
  const noPos = document.createElement("div");
  noPos.className = "position-card-no";
  const noLabel = document.createElement("span");
  noLabel.textContent = "NO";
  const noStrong = document.createElement("strong");
  noStrong.append(duskAmountNode(position?.[1] ?? 0, { decimals: 4 }));
  noPos.append(noLabel, noStrong);
  posVals.append(yesPos, noPos);
  posCard.append(posLabel, posVals);

  function updatePositionCard(next) {
    yesStrong.replaceChildren(duskAmountNode(next?.[0] ?? 0, { decimals: 4 }));
    noStrong.replaceChildren(duskAmountNode(next?.[1] ?? 0, { decimals: 4 }));
  }

  const statsHost = document.createElement("div");
  primary.append(statsHost);
  function paintStats() {
    const stats = document.createElement("div");
    stats.className = "stats-strip";

    const volTotalLux = market.volume != null ? market.volume : breakdown.yesVol + breakdown.noVol;
    stats.append(
      splitStat(
        "Volume",
        duskAmountNode(volTotalLux),
        duskAmountNode(breakdown.yesVol),
        duskAmountNode(breakdown.noVol)
      )
    );

    const tradeTotal =
      market.trade_count != null ? String(market.trade_count) : String(breakdown.total);
    stats.append(
      splitStat("Trades", tradeTotal, String(breakdown.yesCount), String(breakdown.noCount))
    );

    if (info?.yes_reserve != null && info?.no_reserve != null) {
      const poolSum = BigInt(info.yes_reserve) + BigInt(info.no_reserve);
      stats.append(
        splitStat(
          "Pool",
          duskAmountNode(poolSum),
          duskAmountNode(info.yes_reserve),
          duskAmountNode(info.no_reserve)
        )
      );
    }

    if (closeBlock != null) {
      const closeWrap = document.createElement("div");
      closeWrap.className = "stat-value-stack";
      const blockLine = document.createElement("div");
      blockLine.className = "stat-total";
      blockLine.textContent = `Block ${Number(closeBlock).toLocaleString()}`;
      closeWrap.append(blockLine);
      if (blockHeight != null) {
        const left = Number(closeBlock) - blockHeight;
        const rem = document.createElement("div");
        rem.className = "stat-sub";
        rem.textContent =
          left > 0
            ? `${left.toLocaleString()} blocks left`
            : left === 0
              ? "Closing now"
              : "Past close";
        closeWrap.append(rem);
      }
      stats.append(statCell("Closes", closeWrap));
    }
    statsHost.replaceChildren(stats);
  }
  paintStats();

  const chartHost = document.createElement("div");
  primary.append(chartHost);
  function paintChart() {
    chartHost.replaceChildren();
    const priceChart = renderPriceChart(history);
    if (priceChart) {
      chartHost.append(priceChart);
    } else {
      chartHost.append(
        EmptyState({
          title: enrichDone ? "No price history yet" : "Loading price history…",
          description: enrichDone
            ? "Chart fills in once there are a couple of trades."
            : "Fetching trades from the gateway.",
        })
      );
    }
  }
  paintChart();

  const resolutionHost = document.createElement("div");
  primary.append(resolutionHost);
  function paintResolution() {
    resolutionHost.replaceChildren(
      renderResolutionPanel({
        marketId,
        resolution: resolutionView,
        status,
        info,
        wallet: getWallet(),
        log,
        onRefresh,
      })
    );
  }
  paintResolution();

  primary.append(posCard, renderLiquidityExplainer());

  const below = document.createElement("div");
  below.className = "detail-below";

  const paramsHost = document.createElement("div");
  below.append(paramsHost);
  function paintParams() {
    paramsHost.replaceChildren();
    const paramsCard = document.createElement("div");
    paramsCard.className = "info-card params-card";
    const paramsH = document.createElement("strong");
    paramsH.textContent = "Parameters";
    paramsCard.append(paramsH);
    const paramsGrid = document.createElement("div");
    paramsGrid.className = "params-grid";
    const paramPairs = [];
    if (market.initial_liquidity != null) {
      paramPairs.push(["Initial liquidity", duskAmountNode(market.initial_liquidity)]);
    }
    if (info) {
      paramPairs.push(["YES reserve", duskAmountNode(info.yes_reserve)]);
      paramPairs.push(["NO reserve", duskAmountNode(info.no_reserve)]);
      if (info.min_bond != null) {
        paramPairs.push(["Min bond", duskAmountNode(info.min_bond)]);
      }
      if (info.challenge_window_blocks != null) {
        paramPairs.push(["Challenge window", `${info.challenge_window_blocks} blocks`]);
      }
      if (info.review_window_blocks != null) {
        paramPairs.push(["Review window", `${info.review_window_blocks} blocks`]);
      }
    }
    if (market.created_block != null) {
      paramPairs.push(["Created", `Block ${Number(market.created_block).toLocaleString()}`]);
    }
    for (const [label, value] of paramPairs) {
      paramsGrid.append(statCell(label, value));
    }
    if (paramPairs.length) {
      paramsCard.append(paramsGrid);
      paramsHost.append(paramsCard);
    }
  }
  paintParams();

  const tapeSec = document.createElement("section");
  tapeSec.className = "detail-section";
  const tapeH = document.createElement("h3");
  tapeH.textContent = "Trade tape";
  tapeSec.append(tapeH);

  const tapeHost = document.createElement("div");
  tapeSec.append(tapeHost);

  function formatTapePrice(t) {
    const amount = t.amount != null ? Number(t.amount) : NaN;
    const sharesOrOut = t.shares_or_out != null ? Number(t.shares_or_out) : NaN;
    if (!Number.isFinite(amount) || !Number.isFinite(sharesOrOut) || amount <= 0 || sharesOrOut <= 0) {
      return "-";
    }
    // Both legs are lux-denominated; ratio is a 0-1 price → show as ¢.
    const fraction = t.is_buy ? amount / sharesOrOut : sharesOrOut / amount;
    if (!Number.isFinite(fraction) || fraction < 0) return "-";
    const cents = Math.round(fraction * 1000) / 10;
    return `${cents.toFixed(1)}¢`;
  }

  function formatTapePaid(t) {
    if (t.is_buy) {
      return t.amount != null ? luxToDusk(t.amount) : "-";
    }
    return t.shares_or_out != null ? luxToDusk(t.shares_or_out) : "-";
  }

  function formatTapeShares(t) {
    // 1 share = 1 lux - show as DUSK, not raw lux integers.
    const raw = t.is_buy ? t.shares_or_out : t.amount;
    return raw != null ? duskAmountNode(raw, { decimals: 4 }) : "-";
  }

  function renderTape(list) {
    tapeHost.replaceChildren();
    if (!list.length) {
      tapeHost.append(
        EmptyState({
          title: enrichDone ? "No trades" : "Loading trades…",
          description: enrichDone ? "Be the first." : "Fetching the tape from the gateway.",
        })
      );
      return;
    }
    const wrap = document.createElement("div");
    wrap.className = "tape-wrap";
    wrap.append(
      Table({
        columns: [
          { header: "Block", key: "block", render: (t) => (t.block_number != null ? String(t.block_number) : "…") },
          {
            header: "Tx",
            render: (t) => renderTxCell(t.transaction_hash, { keep: 10 }),
          },
          { header: "Side", render: (t) => (t.is_buy ? "buy" : "sell") },
          {
            header: "Outcome",
            render: (t) => {
              const span = document.createElement("span");
              const yes = t.outcome === 0;
              span.className = yes ? "tape-outcome tape-outcome--yes" : "tape-outcome tape-outcome--no";
              span.textContent = yes ? "YES" : "NO";
              return span;
            },
          },
          { header: "Paid / received", render: (t) => formatTapePaid(t) },
          { header: "Shares", render: (t) => formatTapeShares(t) },
          { header: "Price", render: (t) => formatTapePrice(t) },
          {
            header: "Commitment",
            render: (t) => trunc(t.trader_commitment, 10),
          },
        ],
        rows: list,
      })
    );
    tapeHost.append(wrap);
  }
  renderTape(trades);
  below.append(tapeSec);

  const tradeCol = document.createElement("div");
  tradeCol.className = "detail-trade-col";
  const tradePanel = document.createElement("div");
  tradePanel.className = "detail-trade-panel";

  async function appendTradeAndRefresh(result) {
    // Optimistic tape row immediately.
    const optimistic = {
      block_number: null,
      transaction_hash: result.hash,
      is_buy: result.isBuy,
      outcome: result.outcome,
      amount: result.amount,
      shares_or_out: result.sharesOrOut,
      implied_price: null,
      trader_commitment: result.traderCommitment,
    };
    const normHash = (h) =>
      String(h || "")
        .replace(/^0x/i, "")
        .toLowerCase();
    const txHash = normHash(result.hash);

    trades = [optimistic, ...trades.filter((t) => normHash(t.transaction_hash) !== txHash)];
    renderTape(trades);

    // Refresh position from chain.
    try {
      const dusk = await getChainApp();
      const key = loadTradingKey();
      if (key?.commitment_hex) {
        const commitment = Array.from(hexToBytes(key.commitment_hex));
        const pos = await dusk.readContract({
          contract: "predictionMarket",
          functionName: "position_of",
          args: [marketId, commitment],
        });
        updatePositionCard(pos);
      }
    } catch {
      /* keep optimistic UI */
    }

    // Poll gateway for the real indexed row (no full page refresh).
    const cfg = loadBackendConfig();
    for (let i = 0; i < 12; i++) {
      await new Promise((r) => setTimeout(r, 1500));
      try {
        const fresh = await listMarketTrades(cfg, marketId, 200);
        const found = fresh.find((t) => normHash(t.transaction_hash) === txHash);
        if (found) {
          trades = fresh;
          renderTape(trades);
          break;
        }
        // Keep optimistic row until indexed; refresh other tape rows underneath.
        trades = [
          optimistic,
          ...fresh.filter((t) => normHash(t.transaction_hash) !== txHash),
        ];
        renderTape(trades);
      } catch {
        /* retry */
      }
    }
  }

  const ticketNode = renderTradeTicket({
    marketId,
    log,
    yesPercent: pct,
    yesReserve: info?.yes_reserve ?? null,
    noReserve: info?.no_reserve ?? null,
    marketStatus: status,
    closeBlock,
    blockHeight,
    onCollateralChange,
    onDone: async (result) => {
      log?.(`trade confirmed ${result?.hash ?? ""}`);
      if (result) await appendTradeAndRefresh(result);
    },
  });
  tradePanel.append(ticketNode);
  tradeCol.append(tradePanel);

  // Top row only: hero | trade (sticky scoped here). Below: full-width params + tape.
  const top = document.createElement("div");
  top.className = "detail-top";
  top.append(primary, tradeCol);
  layout.append(top, below);
  root.append(layout);

  async function refreshLiveData() {
    try {
      const dusk = await getChainApp();
      const [freshInfo, freshResolution] = await Promise.all([
        dusk.readContract({ contract: "predictionMarket", functionName: "market_info", args: marketId }),
        dusk.readContract({ contract: "predictionMarket", functionName: "resolution_of", args: marketId }),
      ]);
      info = freshInfo;
      resolution = freshResolution;
      try {
        priceYes = await dusk.readContract({
          contract: "predictionMarket",
          functionName: "price_yes",
          args: marketId,
        });
      } catch {
        priceYes = null;
      }
    } catch (e) {
      log?.(`live refresh: on-chain read failed (${e.message || e})`);
      return; // keep showing the last good data rather than half-updating
    }
    computeDerived();
    paintLifecycle();
    paintOdds();
    paintResolution();
    paintParams();
    ticketNode.updateMarketData({
      yesPercent: pct,
      yesReserve: info?.yes_reserve ?? null,
      noReserve: info?.no_reserve ?? null,
      marketStatus: status,
      closeBlock,
      blockHeight,
    });
  }

  async function refreshTapeAndStats() {
    try {
      const [freshTrades, freshHistory] = await Promise.all([
        listMarketTrades(cfg, marketId, 200),
        listMarketHistory(cfg, marketId).catch(() => history),
      ]);
      if (cancelled) return;
      trades = freshTrades;
      history = freshHistory;
      breakdown = outcomeBreakdown(trades);
      paintStats();
      paintChart();
      renderTape(trades);
    } catch (e) {
      log?.(`live tape refresh: ${e.message || e}`);
    }
  }

  async function cheapCheck() {
    // Never let a poll tick throw silently (pollCached's interval path
    // swallows fetcher rejections) - log so a real bug here is visible
    // instead of just "polling mysteriously stopped updating."
    try {
      const [freshMarket, freshHeight] = await Promise.all([
        getMarket(cfg, marketId).catch(() => market),
        getBlockHeight().catch(() => blockHeight),
      ]);
      market = freshMarket;
      blockHeight = freshHeight;
      const signature = marketSignature(market);
      if (signature !== lastSignature) {
        lastSignature = signature;
        await Promise.all([refreshLiveData(), refreshTapeAndStats()]);
        return;
      }
      // Nothing chain-gateway has indexed changed, but blockHeight moving
      // can still flip a purely time-based transition (e.g. open ->
      // pending_no once past close_block) - cheap to reflect without a
      // full on-chain read.
      const prevStatus = status;
      computeDerived();
      if (status !== prevStatus) {
        paintLifecycle();
        paintResolution();
      }
      ticketNode.updateMarketData({ marketStatus: status, closeBlock, blockHeight });
    } catch (e) {
      log?.(`live poll tick failed: ${e.message || e}`);
    }
  }

  const SAFETY_NET_POLL_MS = 45000;
  const SSE_DOWN_POLL_MS = 8000;

  function marketSignature(m) {
    return `${m?.status ?? ""}|${m?.trade_count ?? ""}|${m?.volume ?? ""}`;
  }
  let lastSignature = marketSignature(market);

  let pollUnsub = pollCached(`pm-market-detail-${marketId}`, cheapCheck, {
    // Start on the faster interval until SSE proves healthy (open event).
    intervalMs: SSE_DOWN_POLL_MS,
  });

  function setSafetyPollMs(ms) {
    pollUnsub?.();
    pollUnsub = pollCached(`pm-market-detail-${marketId}`, cheapCheck, {
      intervalMs: ms,
    });
  }

  let liveSource = null;
  let sseHealthy = false;
  if (typeof EventSource === "function") {
    try {
      liveSource = new EventSource(`${cfg.url}/markets/${marketId}/stream`);
      liveSource.addEventListener("open", () => {
        sseHealthy = true;
        setSafetyPollMs(SAFETY_NET_POLL_MS);
        log?.("live push connected");
      });
      liveSource.addEventListener("changed", () => {
        if (!sseHealthy) {
          sseHealthy = true;
          setSafetyPollMs(SAFETY_NET_POLL_MS);
        }
        cheapCheck().catch((e) => log?.(`live push tick failed: ${e.message || e}`));
      });
      // EventSource reconnects natively on error (with backoff). While it's
      // down (or 404ing on a stale gateway), keep the faster safety poll.
      liveSource.addEventListener("error", () => {
        if (sseHealthy) {
          sseHealthy = false;
          setSafetyPollMs(SSE_DOWN_POLL_MS);
          log?.("live push error - faster safety poll until reconnect");
        }
      });
    } catch (e) {
      log?.(`live push unavailable, relying on the poll safety net: ${e.message || e}`);
      liveSource = null;
    }
  }

  async function enrichFromChainAndTape() {
    const tapeP = Promise.all([
      listMarketTrades(cfg, marketId, 200).catch((e) => {
        log?.(`tape: ${e.message || e}`);
        return trades;
      }),
      listMarketHistory(cfg, marketId).catch((e) => {
        log?.(`history: ${e.message || e}`);
        return history;
      }),
    ]);
    const chainP = (async () => {
      const dusk = await getChainApp();
      const key = loadTradingKey();
      const commitment = key?.commitment_hex
        ? Array.from(hexToBytes(key.commitment_hex))
        : null;
      const [freshInfo, freshResolution, freshPrice, freshPos, freshHeight] = await Promise.all([
        dusk.readContract({
          contract: "predictionMarket",
          functionName: "market_info",
          args: marketId,
        }),
        dusk.readContract({
          contract: "predictionMarket",
          functionName: "resolution_of",
          args: marketId,
        }),
        dusk
          .readContract({
            contract: "predictionMarket",
            functionName: "price_yes",
            args: marketId,
          })
          .catch(() => null),
        commitment
          ? dusk.readContract({
              contract: "predictionMarket",
              functionName: "position_of",
              args: [marketId, commitment],
            })
          : Promise.resolve(position),
        getBlockHeight().catch(() => null),
      ]);
      return {
        info: freshInfo,
        resolution: freshResolution,
        priceYes: freshPrice,
        position: freshPos,
        blockHeight: freshHeight,
      };
    })().catch((e) => {
      chainErr = e.message || String(e);
      log?.(`on-chain reads failed: ${chainErr}`);
      return null;
    });

    const [[freshTrades, freshHistory], chain] = await Promise.all([tapeP, chainP]);
    if (cancelled) return;
    trades = freshTrades;
    history = freshHistory;
    breakdown = outcomeBreakdown(trades);
    if (chain) {
      info = chain.info;
      resolution = chain.resolution;
      priceYes = chain.priceYes;
      position = chain.position ?? position;
      blockHeight = chain.blockHeight;
      chainErr = null;
    }
    enrichDone = true;
    computeDerived();
    selectedPhaseIdx = statusIdx;
    paintChainErr();
    paintLifecycle();
    paintOdds();
    paintStats();
    paintResolution();
    paintChart();
    paintParams();
    renderTape(trades);
    updatePositionCard(position);
    ticketNode.updateMarketData({
      yesPercent: pct,
      yesReserve: info?.yes_reserve ?? null,
      noReserve: info?.no_reserve ?? null,
      marketStatus: status,
      closeBlock,
      blockHeight,
    });
  }

  void enrichFromChainAndTape();

  registerCleanup?.(() => {
    cancelled = true;
    pollUnsub?.();
    liveSource?.close();
  });

  return root;
}
